import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { RouterProvider } from "react-router/dom";

import { createBrowserRouter } from "react-router";
import { Layout } from "@/components/layout";
import { paths } from "@/config/paths";
import AppRootErrorBoundary from "./components/app-root-error-boundary";

const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter(
    [
      {
        element: <Layout />,
        children: [
          {
            path: paths.home.path,
            lazy: () => import("@/routes/login/login").then(convert(queryClient)),
            ErrorBoundary: AppRootErrorBoundary,
          },
          {
            path: paths.register.path,
            lazy: () => import("@/routes/register/register").then(convert(queryClient)),
            ErrorBoundary: AppRootErrorBoundary,
          },
          {
            path: paths.finishRegistration.path,
            lazy: () => import("@/routes/finish-registration/finish-registration").then(convert(queryClient)),
            ErrorBoundary: AppRootErrorBoundary,
          },
          {
            path: paths.subscriptionRequired.path,
            lazy: () => import("@/routes/subscription-required/subscription-required").then(convert(queryClient)),
            ErrorBoundary: AppRootErrorBoundary,
          },
          {
            path: paths.regionUnavailable.path,
            lazy: () => import("@/routes/region-unavailable/region-unavailable").then(convert(queryClient)),
            ErrorBoundary: AppRootErrorBoundary,
          },
          {
            path: paths.passwordReset.path,
            lazy: () => import("@/routes/password-reset/password-reset").then(convert(queryClient)),
            ErrorBoundary: AppRootErrorBoundary,
          },
          {
            path: paths.code.path,
            lazy: () => import("@/routes/code/code").then(convert(queryClient)),
            ErrorBoundary: AppRootErrorBoundary,
          },
          {
            path: "*",
            lazy: () => import("@/routes/not-found/not-found").then(convert(queryClient)),
          },
        ],
      },
    ],
    {
      basename: paths.base,
    }
  );

export const AppRouter = () => {
  const queryClient = useQueryClient();
  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
